import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Checkbox,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import type { RoleGrant, RoleVocabularyEntry } from '@/types'

interface GrantsMatrixProps {
	value:      RoleGrant[]
	onChange:   (grants: RoleGrant[]) => void
	vocabulary: RoleVocabularyEntry[]
	disabled?:  boolean
}

/**
 * A role's grants as a table: resources down, actions across, and in each
 * cell the row scopes the action covers. The vocabulary comes from the
 * gateway, so a cell exists only where the services can enforce it.
 */
export function GrantsMatrix({ value, onChange, vocabulary, disabled }: GrantsMatrixProps) {
	const { t } = useTranslation()
	// Every action some resource has, in the vocabulary's order.
	const actions = useMemo(
		() => [...new Set(vocabulary.flatMap((entry) => entry.actions))],
		[vocabulary],
	)

	function scopesOf(resource: string, action: string): string[] {
		return value.find((g) => g.resource === resource && g.action === action)?.scopes ?? []
	}

	function toggle(entry: RoleVocabularyEntry, action: string, scope: string, checked: boolean) {
		const chosen = new Set(scopesOf(entry.resource, action))
		if (checked) chosen.add(scope)
		else chosen.delete(scope)
		const rest = value.filter((g) => !(g.resource === entry.resource && g.action === action))
		onChange(
			chosen.size
				? [...rest, { resource: entry.resource, action, scopes: entry.scopes.filter((s) => chosen.has(s)) }]
				: rest,
		)
	}

	return (
		<div className="overflow-x-auto rounded-md border border-(--color-border)">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="px-3" />
						{actions.map((action) => (
							<TableHead key={action} className="px-3">
								{t(`admin.roles.actionsMatrix.${action}`, { defaultValue: action })}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{vocabulary.map((entry) => (
						<TableRow key={entry.resource}>
							<TableCell className="px-3 align-top font-medium">
								{t(`admin.roles.resources.${entry.resource}`, { defaultValue: entry.resource })}
							</TableCell>
							{actions.map((action) => (
								<TableCell key={action} className="px-3 align-top">
									{entry.actions.includes(action) ? (
										<div className="flex flex-col gap-1.5">
											{entry.scopes.map((scope) => (
												<label key={scope} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
													<Checkbox
														checked={scopesOf(entry.resource, action).includes(scope)}
														onCheckedChange={(checked) => toggle(entry, action, scope, checked === true)}
														disabled={disabled}
													/>
													{t(`admin.roles.scopes.${scope}`, { defaultValue: scope })}
												</label>
											))}
										</div>
									) : (
										<span className="text-(--color-muted-foreground)">—</span>
									)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
